import { useEffect, useMemo, useRef, useState } from 'react';
import type { EventSummary, EventsPage } from '@finder/contracts';
import { distanceLabel, locationPrecisionLabel } from './format.js';
import { shortPlace } from './short-place.js';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  boundsCenter,
  clusterOffset,
  fitZoom,
  projectPoint,
  tilesForViewport,
  worldXToLon,
  worldYToLat,
  lonToWorldX,
  latToWorldY,
  type LngLat,
} from './map-tiles.js';
import { projectMap } from './projections.js';

const MAP_HEIGHT = 320;

/**
 * Map of the current result set. Markers come from `projectMap()` so the map
 * and the list share ids and server order. Tiles are OpenStreetMap rasters
 * (no API key). Pan and zoom are instant — there is no animated camera, so
 * `prefers-reduced-motion` has nothing to suppress here.
 */
export function EventMap({
  page,
  events,
  origin,
  originLabel = 'Seattle Center',
  distanceVoice = 'place',
  selectedId,
  onSelect,
}: {
  page: EventsPage;
  events: readonly EventSummary[];
  origin: LngLat;
  /** Visible origin pin. "You" after GPS, otherwise the place name. */
  originLabel?: string;
  distanceVoice?: 'gps' | 'place';
  selectedId: string | null;
  onSelect: (canonicalId: string) => void;
}): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [view, setView] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  const fittedKey = useRef('');

  const markers = useMemo(
    () => projectMap({ ...page, events: [...events], resultCount: events.length }),
    [page, events],
  );
  const byId = useMemo(() => new Map(events.map((e) => [e.canonicalId, e])), [events]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth;
      setWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (width <= 0) return;
    const key = `${width}:${markers.map((m) => m.canonicalId).join('|')}:${origin.lat},${origin.lng}`;
    if (fittedKey.current === key) return;
    fittedKey.current = key;
    const points = [...markers.map((m) => ({ lat: m.lat, lng: m.lng })), origin];
    const zoom = fitZoom(points, width, MAP_HEIGHT);
    const center = boundsCenter(points);
    setView({ lat: center.lat, lng: center.lng, zoom });
  }, [markers, origin, width]);

  const tiles = useMemo(() => {
    if (!view || width <= 0) return [];
    return tilesForViewport(view, view.zoom, width, MAP_HEIGHT);
  }, [view, width]);

  const groups = useMemo(() => {
    const buckets = new Map<string, string[]>();
    for (const marker of markers) {
      const key = `${marker.lat.toFixed(4)},${marker.lng.toFixed(4)}`;
      const list = buckets.get(key);
      if (list) list.push(marker.canonicalId);
      else buckets.set(key, [marker.canonicalId]);
    }
    return buckets;
  }, [markers]);

  function panBy(dx: number, dy: number): void {
    setView((current) => {
      if (!current) return current;
      return {
        ...current,
        lng: worldXToLon(lonToWorldX(current.lng, current.zoom) - dx, current.zoom),
        lat: worldYToLat(latToWorldY(current.lat, current.zoom) - dy, current.zoom),
      };
    });
  }

  function zoomBy(delta: number): void {
    setView((current) => {
      if (!current) return current;
      const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, current.zoom + delta));
      return zoom === current.zoom ? current : { ...current, zoom };
    });
  }

  const drag = useRef<{ x: number; y: number; lat: number; lng: number; zoom: number } | null>(
    null,
  );

  return (
    <div className="event-map">
      <p className="map-count">
        {markers.length === 0
          ? 'No events with a mapped location'
          : `${markers.length} ${markers.length === 1 ? 'event' : 'events'} on the map`}
        {markers.length > 0 && events.length > markers.length
          ? ` · ${events.length - markers.length} without a mapped location`
          : null}
      </p>
      <div className="map-frame" ref={frameRef}>
        <div
          className="map-surface"
          role="region"
          aria-label="Map of events near you"
          aria-describedby="map-help"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget || !view) return;
            const step = 80;
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              panBy(step, 0);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              panBy(-step, 0);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              panBy(0, step);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              panBy(0, -step);
            } else if (e.key === '+' || e.key === '=') {
              e.preventDefault();
              zoomBy(1);
            } else if (e.key === '-' || e.key === '_') {
              e.preventDefault();
              zoomBy(-1);
            }
          }}
          onPointerDown={(e) => {
            if (!view || e.button !== 0) return;
            if ((e.target as HTMLElement).closest('button, a')) return;
            drag.current = { x: e.clientX, y: e.clientY, ...view };
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            const start = drag.current;
            if (!start) return;
            const dx = e.clientX - start.x;
            const dy = e.clientY - start.y;
            setView({
              zoom: start.zoom,
              lng: worldXToLon(lonToWorldX(start.lng, start.zoom) - dx, start.zoom),
              lat: worldYToLat(latToWorldY(start.lat, start.zoom) - dy, start.zoom),
            });
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
        >
          <p id="map-help" className="visually-hidden">
            Arrow keys pan when the map is focused. Plus and minus zoom. Each event is a button that
            opens why it qualifies as free.
          </p>
          {tiles.map((tile) => (
            <img
              key={tile.key}
              className="map-tile"
              alt=""
              src={tile.url}
              width={256}
              height={256}
              draggable={false}
              style={{ left: `${tile.left}px`, top: `${tile.top}px` }}
            />
          ))}
          {view ? (
            <div
              className="map-you"
              role="img"
              aria-label={
                originLabel === 'You' ? 'Searching from you' : `Searching from ${originLabel}`
              }
              style={pinStyle(projectPoint(origin, view, view.zoom, width, MAP_HEIGHT))}
            >
              {originLabel}
            </div>
          ) : null}
          {view
            ? markers.map((marker) => {
                const event = byId.get(marker.canonicalId);
                const key = `${marker.lat.toFixed(4)},${marker.lng.toFixed(4)}`;
                const ids = groups.get(key) ?? [marker.canonicalId];
                const offset = clusterOffset(
                  ids.length,
                  Math.max(0, ids.indexOf(marker.canonicalId)),
                );
                const point = projectPoint(marker, view, view.zoom, width, MAP_HEIGHT);
                const title = event?.title ?? marker.title;
                const place = shortPlace(event?.venueName);
                const distance = event ? distanceLabel(event.distanceMiles, distanceVoice) : '';
                const precisionLabel = locationPrecisionLabel(event?.locationPrecision);
                const label = [title, place, distance, precisionLabel].filter(Boolean).join(', ');
                const selected = selectedId === marker.canonicalId;
                return (
                  <button
                    key={marker.canonicalId}
                    type="button"
                    className={`map-marker${selected ? ' map-marker--selected' : ''}${precisionLabel ? ' map-marker--approximate' : ''}`}
                    style={pinStyle({ x: point.x + offset.dx, y: point.y + offset.dy })}
                    aria-label={`${label}. Show why it qualifies.`}
                    aria-pressed={selected}
                    onClick={() => onSelect(marker.canonicalId)}
                  >
                    <span className="map-marker__dot" aria-hidden="true" />
                    {precisionLabel ? (
                      <span className="map-marker__approx">{precisionLabel}</span>
                    ) : null}
                    <span className="map-marker__label">
                      <span className="map-marker__title">{title}</span>
                      {place ? <span className="map-marker__place"> · {place}</span> : null}
                    </span>
                  </button>
                );
              })
            : null}
          <div className="map-zoom">
            <button type="button" onClick={() => zoomBy(1)} aria-label="Zoom in">
              +
            </button>
            <button type="button" onClick={() => zoomBy(-1)} aria-label="Zoom out">
              −
            </button>
          </div>
          <p className="map-attrib">
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
            >
              © OpenStreetMap
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function pinStyle(point: { x: number; y: number }): { left: string; top: string } {
  return { left: `${point.x}px`, top: `${point.y}px` };
}
